/**
 * services/sync/sync.service.ts
 * The outbox: what this subledger has told Payroll and Billing, and whether it
 * landed. Gated on both connectors being present (412 + `connectors`).
 */
import axiosInstance from '../../api/axiosInstance';
import { Cash_Sync_Retry_Url, Cash_Sync_Url } from '../../api/endpoints';
import { parseResponse } from '../../schemas/common';
import { syncResponseSchema, type SyncResponse, type SyncRow } from '../../schemas/sync';

/**
 * The outbox, its dead-letter count, and the gate map.
 *
 * The WHOLE payload is returned rather than just the rows: this endpoint
 * answers 200 even when Payroll and Billing are unconnected, and carries the
 * gate in the body. Discarding it here would leave the screen unable to tell
 * "nothing has been sent yet" from "nothing can be sent yet".
 */
export async function fetchSyncLog(status?: string): Promise<SyncResponse> {
  const { data } = await axiosInstance.get(Cash_Sync_Url, { params: { status } });
  return parseResponse(syncResponseSchema, data, 'GET /v1/sync');
}

/**
 * Re-send a failed event.
 *
 * The retry carries the event's OWN identity as the idempotency key rather than
 * a fresh one, so the destination collapses a duplicate delivery instead of
 * booking the same journal entry twice. That is the one place in this app where
 * the interceptor's generated key would be wrong.
 */
export async function retrySync(id: string): Promise<unknown> {
  const { data } = await axiosInstance.post(
    Cash_Sync_Retry_Url(id),
    {},
    { headers: { 'x-idempotency-key': `sync-retry:${id}` } },
  );
  return data;
}

export type { SyncRow };
